import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import type { MojeWyszukiwanie } from '@/hooks/useMojeWyszukiwanie';
import {
  DEFAULT_FILTERS,
  bezZamiaru,
  filtersToParams,
  paramsToFilters,
  paramsToWidokMapy,
  stanWpisuZProfilem,
  wpisDoPrzepisania,
  zProfiluZeStanu,
  zamiarMojegoWyszukiwania,
  zamiarPaneluFiltrow,
  type WidokMapy,
} from '@/lib/serpUrl';

/**
 * Po zdjęciu chipa „Moje wyszukiwanie” profil NIE wraca do końca sesji w tej karcie, inaczej
 * każde wejście na `/search` przywracałoby filtry, które człowiek przed chwilą zdjął. Wyjątek:
 * ŚWIADOME wejście („Szukaj Mieszkania” w nagłówku, `ZAMIAR_MOJEGO_WYSZUKIWANIA`).
 */
const KLUCZ_ODRZUCONY = 'flh-moje-wyszukiwanie-odrzucone';

/** Adres bez filtrów: tylko wtedy podstawiamy profil. `proto` to przełącznik makiet, nie filtr. */
function golyAdres(params: URLSearchParams): boolean {
  const p = new URLSearchParams(params);
  p.delete('proto');
  return [...p.keys()].length === 0;
}

const rowne = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * STAN WYSZUKIWARKI ZSYNCHRONIZOWANY Z ADRESEM: filtry, kadr mapy, chip „Moje wyszukiwanie”
 * i podstawienie profilu (SRCH-01). Wydzielony z SearchPage 1.10, żeby dało się go testować
 * z prawdziwym routerem (useStanWyszukiwania.test.tsx).
 *
 * DLACZEGO TAK (zgłoszenie Maćka 1.10, „disco”). Do 1.10 synchronizowały się dwa efekty:
 * filtry → adres oraz adres → filtry, a pierwszy reagował TAKŻE na zmianę adresu. Gdy adres
 * zmieniał się z zewnątrz przy zamontowanej wyszukiwarce („wstecz”, link w nagłówku), w tym
 * samym przebiegu drugi efekt brał filtry z nowego adresu, a pierwszy zapisywał stary adres
 * ze starych filtrów. W następnym przebiegu role się odwracały i tak bez końca: adres i lista
 * skakały co chwilę między dwoma stanami (test: ponad 200 zmian adresu).
 *
 * Teraz kierunki są rozdzielone:
 *  - filtry → adres: TYLKO gdy zmieniły się filtry (albo kadr, chip, układ); adres i stan wpisu
 *    czytamy z bieżących wartości, a każdy własny zapis zapamiętujemy;
 *  - adres → stan: przy każdej nawigacji. Własny zapis rozpoznajemy i pomijamy (echo); nawigacja
 *    z zewnątrz wygrywa w całości: filtry, kadr i chip biorą się z wpisu.
 *  - „wejście bez parametrów” liczy się przy KAŻDEJ nawigacji z zewnątrz, nie tylko przy
 *    montowaniu: goły `/search` z nagłówka przy otwartej wyszukiwarce też podstawia profil.
 */
export function useStanWyszukiwania({
  isDesktop,
  mojeWyszukiwanie,
}: {
  isDesktop: boolean;
  mojeWyszukiwanie: Pick<MojeWyszukiwanie, 'filters' | 'hasAny'> | undefined;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [filters, setFiltersState] = useState<SourcePostFilters>(() => paramsToFilters(searchParams));
  /**
   * WIDOK MAPY W ADRESIE (etap H): kadr i otwarta oferta. Osobny stan, a nie pole filtrów:
   * filtry wchodzą do klucza zapytań, więc przesunięcie mapy odpalałoby pobieranie ofert od nowa.
   */
  const [widokMapy, setWidokMapy] = useState<WidokMapy>(() => paramsToWidokMapy(searchParams));

  const swiadomeNaStarcie = zamiarMojegoWyszukiwania(location.state);
  /** Czekamy na profil, żeby go podstawić: wejście z gołym adresem, dopóki profil nie przyjdzie. */
  const [czekaNaProfil, setCzekaNaProfil] = useState(() => golyAdres(searchParams));
  const [profilOdrzucony, setProfilOdrzucony] = useState(() => {
    if (swiadomeNaStarcie) {
      try { sessionStorage.removeItem(KLUCZ_ODRZUCONY); } catch { /* tryb prywatny */ }
      return false;
    }
    try { return sessionStorage.getItem(KLUCZ_ODRZUCONY) === '1'; } catch { return false; }
  });
  /**
   * „Moje wyszukiwanie” jedzie w STANIE WPISU HISTORII (`location.state.zProfilu`), nie w adresie:
   * wraca przy „wstecz” i odświeżeniu, ale nie jedzie w linku wysłanym komuś innemu.
   */
  const [zProfilu, setZProfilu] = useState(() => zProfiluZeStanu(location.state));

  // Bieżący adres i stan wpisu do odczytu w efekcie filtry → adres, BEZ reagowania na ich zmianę.
  const adresRef = useRef(searchParams);
  adresRef.current = searchParams;
  const stanWpisuRef = useRef<unknown>(location.state);
  stanWpisuRef.current = location.state;
  // `setSearchParams` z react-routera zmienia tożsamość przy KAŻDEJ zmianie adresu; jako
  // zależność efektu filtry → adres przywracałby tylnymi drzwiami reakcję na adres, czyli pętlę.
  const zapiszAdresRef = useRef(setSearchParams);
  zapiszAdresRef.current = setSearchParams;
  /** Adresy zapisane przez nas, jeszcze niepotwierdzone nawigacją: ich powrót to echo, nie zmiana. */
  const naszeZapisy = useRef<string[]>([]);
  /**
   * Czy router chodzi po pasku adresu przeglądarki (BrowserRouter w aplikacji). Sprawdzane raz,
   * przy montowaniu: wtedy ścieżka okna i routera są zgodne. W testach z MemoryRouterem nie są
   * i strażnik niżej się nie włącza.
   */
  const routerWOknie = useRef(typeof window !== 'undefined' && window.location.pathname === location.pathname);

  // Filtry → adres. `replace`, nie `push`: zmiana filtra nie robi wpisu w historii, a jedno
  // przeciągnięcie mapy to kilka `moveend` — „wstecz” cofałoby po pikselu.
  useEffect(() => {
    // PRZEGLĄDARKA JUŻ WYSZŁA Z WYSZUKIWARKI (E2E 5.10, pulpit). Oferta ładuje się leniwie, więc
    // podczas przejścia ta strona jest jeszcze zamontowana; mapa kończy wtedy pierwsze kadrowanie
    // i ten efekt zapisywał kadr z `replace`, czyli PODMIENIAŁ wpis `/offer/…` z powrotem na
    // `/search?…&m=…` — człowiek klikał ofertę i lądował z powrotem na liście. Pasek adresu zmienia
    // się od razu (pushState), router dopiero po załadowaniu strony: rozjazd = nie pisać.
    if (routerWOknie.current && window.location.pathname !== location.pathname) return;
    const nastepny = filtersToParams(filters, widokMapy, { mapaNaEkranie: isDesktop || filters.view === 'map' }).toString();
    const stan = stanWpisuRef.current;
    /**
     * PORÓWNUJEMY Z OSTATNIM WŁASNYM ZAPISEM, nie z adresem routera (flaky E2E 16.10, „Cofnij”).
     * React Router 7 aktualizuje lokalizację w `startTransition`: przeglądarka stoi już na naszym
     * zapisie, `useSearchParams` jeszcze na poprzednim adresie. „Cofnij” w tym oknie wraca do
     * DOKŁADNIE poprzedniego stanu, więc porównanie z adresem routera mówiło „bez zmian”, zapis
     * przepadał, a spóźnione przejście kończyło na adresie wyczyszczonym — rozpoznanym jako echo,
     * więc filtry na ekranie i adres rozjeżdżały się do następnej zmiany.
     */
    const biezacy = naszeZapisy.current.at(-1) ?? adresRef.current.toString();
    if (wpisDoPrzepisania(nastepny, biezacy, stan, zProfilu) || zamiarMojegoWyszukiwania(stan) || zamiarPaneluFiltrow(stan)) {
      naszeZapisy.current.push(nastepny);
      zapiszAdresRef.current(nastepny, { replace: true, state: stanWpisuZProfilem(bezZamiaru(stan), zProfilu) });
    }
    // `location.pathname` czytany wyżej jest stały, póki ta strona jest zamontowana.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, widokMapy, isDesktop, zProfilu]);

  // Adres → stan, przy każdej nawigacji (każdy wpis, także `replace`, ma nowy klucz).
  useEffect(() => {
    const adres = searchParams.toString();
    const i = naszeZapisy.current.indexOf(adres);
    if (i !== -1) {
      // Echo własnego zapisu (także pośredniego, gdy filtry zmieniły się kilka razy z rzędu).
      naszeZapisy.current.splice(0, i + 1);
      return;
    }
    naszeZapisy.current = [];
    // Nawigacja z zewnątrz: adres wygrywa.
    const zAdresu = paramsToFilters(searchParams);
    setFiltersState((f) => (rowne(f, zAdresu) ? f : zAdresu));
    const widokZAdresu = paramsToWidokMapy(searchParams);
    setWidokMapy((w) => (rowne(w, widokZAdresu) ? w : widokZAdresu));
    setZProfilu(zProfiluZeStanu(location.state));
    if (zamiarMojegoWyszukiwania(location.state)) {
      setProfilOdrzucony(false);
      try { sessionStorage.removeItem(KLUCZ_ODRZUCONY); } catch { /* tryb prywatny */ }
    }
    setCzekaNaProfil(golyAdres(searchParams));
    // Tylko nawigacja wyznacza ten efekt; filtry czytamy z adresu, nie z poprzedniego stanu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  /**
   * Podstawienie filtrów z profilu: po tym jak przyjdą, tylko przy wejściu z gołym adresem
   * i tylko gdy człowiek niczego jeszcze nie ruszył. Po podstawieniu adres składa się normalnie,
   * więc stan da się udostępnić linkiem.
   */
  useEffect(() => {
    if (!czekaNaProfil || profilOdrzucony || zProfilu) return;
    if (!mojeWyszukiwanie?.hasAny) return;
    setFiltersState((biezace) => {
      // Jeśli w międzyczasie człowiek sam coś ustawił, profil ustępuje: jego ruch jest świeższą
      // informacją o tym, czego szuka, niż profil sprzed tygodnia.
      const nietkniete = rowne(biezace, DEFAULT_FILTERS);
      return nietkniete ? { ...biezace, ...mojeWyszukiwanie.filters } : biezace;
    });
    setZProfilu(true);
    setCzekaNaProfil(false);
  }, [czekaNaProfil, profilOdrzucony, zProfilu, mojeWyszukiwanie]);

  /** Zdjęcie chipa „Moje wyszukiwanie”: czyścimy filtry i nie wracamy do profilu w tej sesji. */
  const odrzucProfil = () => {
    setZProfilu(false);
    setProfilOdrzucony(true);
    setCzekaNaProfil(false);
    try { sessionStorage.setItem(KLUCZ_ODRZUCONY, '1'); } catch { /* tryb prywatny */ }
    setFiltersState(DEFAULT_FILTERS);
  };

  return { filters, setFilters: setFiltersState, widokMapy, setWidokMapy, zProfilu, odrzucProfil };
}
